/**
 * useBookingSelection Hook
 *
 * Manages multi-select state for booking cards.
 * Provides selection actions and computed selection data.
 */

import {
  addToSelectionAtom,
  clearSelectionAtom,
  editingEmployeeIdAtom,
  enterEmployeeEditModeAtom,
  enterSelectModeAtom,
  exitEmployeeEditModeAtom,
  exitSelectModeAtom,
  hasSelectionAtom,
  previousSelectedCardAtom,
  rangeHoverTargetAtom,
  removeFromSelectionAtom,
  selectModeAtom,
  selectRangeAtom,
  selectedCardIdsAtom,
  selectionCountAtom,
  setRangeHoverTargetAtom,
  toggleCardSelectionAtom,
  toggleSelectModeAtom,
} from "@/store/selectionStore";
import { filterWeekdays, filterWeekends, formatDateKey } from "@/utils/date";
import { compareAsc, isWeekend } from "date-fns";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useMemo, useRef } from "react";
import { useBookings, type BookingCard } from "./useBookings";

/**
 * Selection statistics
 */
export interface SelectionStats {
  cardCount: number;
  uniqueDates: number;
  weekendDays: number;
  totalHours: number;
  employeeCount: number;
  customerCount: number;
}

export function useBookingSelection() {
  // Selection state
  const [selectMode, setSelectMode] = useAtom(selectModeAtom);
  const [selectedIds, setSelectedIds] = useAtom(selectedCardIdsAtom);
  const previousSelectedCard = useAtomValue(previousSelectedCardAtom);
  const selectionCount = useAtomValue(selectionCountAtom);
  const hasSelection = useAtomValue(hasSelectionAtom);

  // Action atoms
  const toggleCardSelection = useSetAtom(toggleCardSelectionAtom);
  const selectRange = useSetAtom(selectRangeAtom);
  const addToSelection = useSetAtom(addToSelectionAtom);
  const removeFromSelection = useSetAtom(removeFromSelectionAtom);
  const clearSelection = useSetAtom(clearSelectionAtom);
  const enterSelectMode = useSetAtom(enterSelectModeAtom);
  const exitSelectMode = useSetAtom(exitSelectModeAtom);
  const toggleSelectMode = useSetAtom(toggleSelectModeAtom);

  // Per-employee edit mode
  const editingEmployeeId = useAtomValue(editingEmployeeIdAtom);
  const enterEmployeeEditMode = useSetAtom(enterEmployeeEditModeAtom);
  const exitEmployeeEditMode = useSetAtom(exitEmployeeEditModeAtom);

  // Range hover preview (Bug #9023)
  const rangeHoverTarget = useAtomValue(rangeHoverTargetAtom);
  const setRangeHoverTarget = useSetAtom(setRangeHoverTargetAtom);

  // Get booking cards from useBookings
  const { bookingCards, getCardHours, getCardById } = useBookings();

  // ============ REFS FOR STABLE CALLBACKS ============
  // These refs allow isSelected and handleCardClick to have stable references
  // while still accessing the latest state values. This enables React.memo
  // to work effectively on child components.

  const selectedIdsRef = useRef(selectedIds);
  selectedIdsRef.current = selectedIds;

  const selectModeRef = useRef(selectMode);
  selectModeRef.current = selectMode;

  const rangeHoverTargetRef = useRef(rangeHoverTarget);
  rangeHoverTargetRef.current = rangeHoverTarget;

  const previousSelectedCardRef = useRef(previousSelectedCard);
  previousSelectedCardRef.current = previousSelectedCard;

  // ============ DERIVED SELECTION DATA ============

  /**
   * Get full BookingCard objects for selected IDs
   */
  const selectedCards = useMemo((): BookingCard[] => {
    return bookingCards.filter((card) => selectedIds.has(card.id));
  }, [bookingCards, selectedIds]);

  /**
   * Compute selection statistics
   */
  const selectionStats = useMemo((): SelectionStats => {
    const uniqueDates = new Set<string>();
    let weekendDays = 0;
    let totalHours = 0;
    const uniqueEmployees = new Set<number>();
    const uniqueCustomers = new Set<number>();

    for (const card of selectedCards) {
      const dateKey = formatDateKey(card.date);
      uniqueDates.add(dateKey);

      if (isWeekend(card.date)) {
        weekendDays++;
      }

      const { totalHours: cardTotal } = getCardHours(card);
      totalHours += cardTotal;

      if (card.employee) {
        uniqueEmployees.add(card.employee.id);
      }
      if (card.customer) {
        uniqueCustomers.add(card.customer.id);
      }
    }

    return {
      cardCount: selectedCards.length,
      uniqueDates: uniqueDates.size,
      weekendDays,
      totalHours,
      employeeCount: uniqueEmployees.size,
      customerCount: uniqueCustomers.size,
    };
  }, [selectedCards, getCardHours]);

  /**
   * Compute IDs of cards in the range preview (between previousSelectedCard and rangeHoverTarget)
   * This is used to show visual feedback when hovering with SHIFT held (Bug #9023)
   */
  const rangePreviewIds = useMemo((): Set<string> => {
    if (!previousSelectedCard || !rangeHoverTarget) {
      return new Set();
    }

    // Find cards and compute range
    const sortedCards = [...bookingCards].sort((a, b) => {
      const dateCompare = compareAsc(a.date, b.date);
      if (dateCompare !== 0) return dateCompare;
      const aEntityId = a.employee?.id ?? a.customer?.id ?? 0;
      const bEntityId = b.employee?.id ?? b.customer?.id ?? 0;
      return aEntityId - bEntityId;
    });

    const prevIndex = sortedCards.findIndex(
      (c) => c.id === previousSelectedCard,
    );
    const targetIndex = sortedCards.findIndex((c) => c.id === rangeHoverTarget);

    if (prevIndex === -1 || targetIndex === -1) {
      return new Set();
    }

    const startIndex = Math.min(prevIndex, targetIndex);
    const endIndex = Math.max(prevIndex, targetIndex);

    const rangeIds = sortedCards
      .slice(startIndex, endIndex + 1)
      .map((c) => c.id);
    return new Set(rangeIds);
  }, [bookingCards, previousSelectedCard, rangeHoverTarget]);

  // ============ SELECTION PREDICATES ============

  /**
   * Check if a card is selected.
   * Uses ref to maintain stable function reference for React.memo optimization.
   */
  const isSelected = useCallback((cardId: string): boolean => {
    return selectedIdsRef.current.has(cardId);
  }, []);

  /**
   * Check if a card is in the range preview (shift-hover preview).
   * Returns true when hovering with SHIFT and card is between previousSelected and hover target.
   */
  const isInRangePreview = useCallback(
    (cardId: string): boolean => {
      return rangePreviewIds.has(cardId);
    },
    [rangePreviewIds],
  );

  /**
   * Check if all visible cards are selected
   */
  const isAllSelected = useMemo((): boolean => {
    if (bookingCards.length === 0) return false;
    return bookingCards.every((card) => selectedIds.has(card.id));
  }, [bookingCards, selectedIds]);

  /**
   * Check if some (but not all) cards are selected
   */
  const isSomeSelected = useMemo((): boolean => {
    if (bookingCards.length === 0) return false;
    const selectedCount = bookingCards.filter((card) =>
      selectedIds.has(card.id),
    ).length;
    return selectedCount > 0 && selectedCount < bookingCards.length;
  }, [bookingCards, selectedIds]);

  // ============ ADVANCED SELECTION ACTIONS ============

  /**
   * Select all visible cards
   */
  const selectAll = useCallback(() => {
    const allIds = bookingCards.map((card) => card.id);
    setSelectedIds(new Set(allIds));
  }, [bookingCards, setSelectedIds]);

  /**
   * Toggle selection of all cards
   */
  const toggleSelectAll = useCallback(() => {
    if (isAllSelected) {
      clearSelection();
    } else {
      selectAll();
    }
  }, [isAllSelected, clearSelection, selectAll]);

  /**
   * Handle shift-click range selection
   * Selects all cards between previous selection and current card
   */
  const selectToCard = useCallback(
    (targetCardId: string) => {
      if (!previousSelectedCard) {
        toggleCardSelection(targetCardId);
        return;
      }

      // Find indices in sorted order
      const sortedCards = [...bookingCards].sort((a, b) => {
        const dateCompare = compareAsc(a.date, b.date);
        if (dateCompare !== 0) return dateCompare;
        // Within same date, sort by employee/customer ID
        const aEntityId = a.employee?.id ?? a.customer?.id ?? 0;
        const bEntityId = b.employee?.id ?? b.customer?.id ?? 0;
        return aEntityId - bEntityId;
      });

      const prevIndex = sortedCards.findIndex(
        (c) => c.id === previousSelectedCard,
      );
      const targetIndex = sortedCards.findIndex((c) => c.id === targetCardId);

      if (prevIndex === -1 || targetIndex === -1) {
        toggleCardSelection(targetCardId);
        return;
      }

      const startIndex = Math.min(prevIndex, targetIndex);
      const endIndex = Math.max(prevIndex, targetIndex);

      const rangeIds = sortedCards
        .slice(startIndex, endIndex + 1)
        .map((c) => c.id);
      addToSelection(rangeIds);
    },
    [bookingCards, previousSelectedCard, toggleCardSelection, addToSelection],
  );

  /**
   * Select cards for a specific date
   */
  const selectByDate = useCallback(
    (date: Date) => {
      const dateStr = formatDateKey(date);
      const dateCards = bookingCards.filter((card) =>
        card.id.startsWith(dateStr),
      );
      const cardIds = dateCards.map((c) => c.id);
      addToSelection(cardIds);
    },
    [bookingCards, addToSelection],
  );

  /**
   * Select cards for a specific employee
   */
  const selectByEmployee = useCallback(
    (employeeId: number) => {
      const employeeCards = bookingCards.filter(
        (card) => card.employee?.id === employeeId,
      );
      const cardIds = employeeCards.map((c) => c.id);
      addToSelection(cardIds);
    },
    [bookingCards, addToSelection],
  );

  /**
   * Select cards for a specific customer
   */
  const selectByCustomer = useCallback(
    (customerId: number) => {
      const customerCards = bookingCards.filter(
        (card) => card.customer?.id === customerId,
      );
      const cardIds = customerCards.map((c) => c.id);
      addToSelection(cardIds);
    },
    [bookingCards, addToSelection],
  );

  /**
   * Select only weekend cards
   */
  const selectWeekends = useCallback(() => {
    const weekendDates = filterWeekends(bookingCards.map((c) => c.date));
    const weekendCards = bookingCards.filter((card) =>
      weekendDates.some((d) => d.getTime() === card.date.getTime()),
    );
    const cardIds = weekendCards.map((c) => c.id);
    selectRange(cardIds);
  }, [bookingCards, selectRange]);

  /**
   * Select only weekday cards
   */
  const selectWeekdays = useCallback(() => {
    const weekdayDates = filterWeekdays(bookingCards.map((c) => c.date));
    const weekdayCards = bookingCards.filter((card) =>
      weekdayDates.some((d) => d.getTime() === card.date.getTime()),
    );
    const cardIds = weekdayCards.map((c) => c.id);
    selectRange(cardIds);
  }, [bookingCards, selectRange]);

  /**
   * Invert current selection
   */
  const invertSelection = useCallback(() => {
    const newSelected = new Set<string>();
    for (const card of bookingCards) {
      if (!selectedIds.has(card.id)) {
        newSelected.add(card.id);
      }
    }
    setSelectedIds(newSelected);
  }, [bookingCards, selectedIds, setSelectedIds]);

  // ============ DAY-OF-WEEK SELECTION ============

  /**
   * Get day of week (0=Monday, 1=Tuesday, ..., 6=Sunday)
   */
  const getDayOfWeek = useCallback((date: Date): number => {
    const day = date.getDay();
    // Convert Sunday=0 to Sunday=6, Monday=1 to Monday=0
    return day === 0 ? 6 : day - 1;
  }, []);

  /**
   * Get cards for a specific day of week
   */
  const getCardsForDayOfWeek = useCallback(
    (dayIndex: number): BookingCard[] => {
      return bookingCards.filter(
        (card) => getDayOfWeek(card.date) === dayIndex,
      );
    },
    [bookingCards, getDayOfWeek],
  );

  /**
   * Check if all cards for a day of week are selected.
   * Note: This function depends on selectedIds directly for reactivity.
   * The selectedIds dependency is needed so the return value updates when selection changes.
   */
  const isDayOfWeekSelected = useCallback(
    (dayIndex: number): boolean => {
      const dayCards = getCardsForDayOfWeek(dayIndex);
      if (dayCards.length === 0) return false;
      return dayCards.every((card) => selectedIds.has(card.id));
    },
    [getCardsForDayOfWeek, selectedIds],
  );

  /**
   * Check if some (but not all) cards for a day of week are selected.
   * Note: This function depends on selectedIds directly for reactivity.
   */
  const isDayOfWeekPartiallySelected = useCallback(
    (dayIndex: number): boolean => {
      const dayCards = getCardsForDayOfWeek(dayIndex);
      if (dayCards.length === 0) return false;
      const selectedCount = dayCards.filter((card) =>
        selectedIds.has(card.id),
      ).length;
      return selectedCount > 0 && selectedCount < dayCards.length;
    },
    [getCardsForDayOfWeek, selectedIds],
  );

  /**
   * Toggle selection for a specific day of week
   * If any cards are unselected, select all. Otherwise deselect all.
   * Uses ref to access latest selectedIds to avoid stale closures with React.memo.
   */
  const toggleDayOfWeek = useCallback(
    (dayIndex: number) => {
      const dayCards = getCardsForDayOfWeek(dayIndex);
      // Use ref to get latest selectedIds, avoiding stale closure issues
      const currentSelectedIds = selectedIdsRef.current;
      const unselectedCards = dayCards.filter(
        (card) => !currentSelectedIds.has(card.id),
      );

      if (unselectedCards.length > 0) {
        // Some unselected - add them all
        addToSelection(unselectedCards.map((c) => c.id));
      } else {
        // All selected - remove them all
        removeFromSelection(dayCards.map((c) => c.id));
      }
    },
    [getCardsForDayOfWeek, addToSelection, removeFromSelection],
  );

  // ============ SELECTION WITH BOOKINGS CHECK ============

  /**
   * Check if any selected cards have bookings
   */
  const hasSelectedCardsWithBookings = useMemo((): boolean => {
    return selectedCards.some((card) => card.bookings.length > 0);
  }, [selectedCards]);

  /**
   * Handle card click with modifiers and optional view details callback.
   * Uses ref to maintain stable function reference for React.memo optimization.
   *
   * When NOT in select mode: calls onViewDetails to show card details
   * When IN select mode: handles selection (toggle, range select with shift)
   *
   * @param cardId - The ID of the clicked card
   * @param modifiers - Keyboard modifiers (shift, ctrl, meta)
   * @param onViewDetails - Optional callback to view card details (used when not in select mode)
   */
  const handleCardClick = useCallback(
    (
      cardId: string,
      modifiers: { shift?: boolean; ctrl?: boolean; meta?: boolean } = {},
      onViewDetails?: (card: BookingCard) => void,
    ) => {
      if (!selectModeRef.current) {
        // Not in select mode - view details instead of selecting
        const card = getCardById(cardId);
        if (card && onViewDetails) {
          onViewDetails(card);
        }
        return;
      }

      // In select mode - handle selection
      if (modifiers.shift) {
        // Shift-click: range selection
        selectToCard(cardId);
      } else if (modifiers.ctrl || modifiers.meta) {
        // Ctrl/Cmd-click: toggle individual
        toggleCardSelection(cardId);
      } else {
        // Regular click: toggle individual
        toggleCardSelection(cardId);
      }
    },
    [getCardById, toggleCardSelection, selectToCard],
  );

  return {
    // State
    selectMode,
    selectedIds,
    selectedCards,
    selectionCount,
    hasSelection,
    selectionStats,
    previousSelectedCard,
    editingEmployeeId,

    // Range preview state (Bug #9023)
    rangeHoverTarget,
    rangePreviewIds,

    // Predicates
    isSelected,
    isInRangePreview,
    isAllSelected,
    isSomeSelected,

    // Basic actions
    setSelectMode,
    toggleCardSelection,
    selectRange,
    addToSelection,
    removeFromSelection,
    clearSelection,
    enterSelectMode,
    exitSelectMode,
    toggleSelectMode,

    // Range preview action (Bug #9023)
    setRangeHoverTarget,

    // Per-employee edit mode
    enterEmployeeEditMode,
    exitEmployeeEditMode,

    // Advanced actions
    selectAll,
    toggleSelectAll,
    selectToCard,
    selectByDate,
    selectByEmployee,
    selectByCustomer,
    selectWeekends,
    selectWeekdays,
    invertSelection,
    handleCardClick,

    // Day-of-week selection
    isDayOfWeekSelected,
    isDayOfWeekPartiallySelected,
    toggleDayOfWeek,

    // Selection info
    hasSelectedCardsWithBookings,
  };
}
